/**
 * Conversation Tabs
 * Tab bar for switching between AI conversations
 * 
 * This is now a pure presentation component.
 * All visible tab state is managed by AIContext.
 */

import { useState, useMemo, useRef, useEffect } from 'react';
import { logger } from '../utils/logger';
import { useAI } from '../contexts/AIContext';
import { conversationsApi } from '../services/api';
import { useConfirm, Button, Input, EmptyState } from '@/components/ui';
import { useArtifactColors } from '../hooks/useArtifactColors';

export default function ConversationTabs() {
  const {
    conversations,
    conversationsLoaded,
    hasMoreConversations,
    loadingMoreConversations,
    loadMoreConversations,
    activeConversationId,
    switchConversation,
    createConversation,
    deleteConversation,
    renameConversation,
    // Visible tabs from context (single source of truth)
    visibleTabIds,
    addVisibleTab,
    removeVisibleTab,
    reorderVisibleTabs,
    // Unlink any conversation
    unlinkConversation,
    // Per-conversation buffers for processing indicators
    conversationBuffers,
  } = useAI();
  
  // Artifact colors for visual pairing of linked tabs
  const { getClassesForArtifact } = useArtifactColors();

  // Debug logging (uncomment if needed)
  // logger.debug('[ConversationTabs] documentLocks:', documentLocks.length, 'activeConversationId:', activeConversationId);
  
  // UI-only local state (not persisted)
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [contextMenuTab, setContextMenuTab] = useState<string | null>(null);
  const [contextMenuPosition, setContextMenuPosition] = useState<{ x: number; y: number } | null>(null);
  const [showHistoryDropdown, setShowHistoryDropdown] = useState(false);
  const [historySearch, setHistorySearch] = useState('');
  const [draggedTabId, setDraggedTabId] = useState<string | null>(null);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);
  const tabRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const historyDropdownRef = useRef<HTMLDivElement | null>(null);
  const historyButtonRef = useRef<HTMLButtonElement | null>(null);
  const confirm = useConfirm();

  // Group conversations for history dropdown by time periods
  const groupedConversations = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    // Get start of current week (Sunday)
    const startOfThisWeek = new Date(today);
    startOfThisWeek.setDate(today.getDate() - today.getDay());
    
    // Get start of last week
    const startOfLastWeek = new Date(startOfThisWeek);
    startOfLastWeek.setDate(startOfLastWeek.getDate() - 7);
    
    // Get start of 2 weeks ago
    const startOfTwoWeeksAgo = new Date(startOfLastWeek);
    startOfTwoWeeksAgo.setDate(startOfTwoWeeksAgo.getDate() - 7);
    
    // Get start of this month
    const startOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    
    // Get start of last month
    const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    
    // Use Map to preserve insertion order
    const groups = new Map<string, typeof conversations>([
      ['Today', []],
      ['Yesterday', []],
      ['This Week', []],
      ['Last Week', []],
      ['Two Weeks Ago', []],
      ['This Month', []],
      ['Last Month', []],
      ['Older', []],
    ]);

    const normalizedSearch = historySearch.trim().toLowerCase();
    const filtered = normalizedSearch
      ? conversations.filter(conv =>
          conv.title.toLowerCase().includes(normalizedSearch) ||
          conv.conversationId.toLowerCase().includes(normalizedSearch)
        )
      : conversations;

    filtered.forEach(conv => {
      const updatedAt = new Date(conv.updatedAt);
      const updatedDateOnly = new Date(updatedAt.getFullYear(), updatedAt.getMonth(), updatedAt.getDate());

      if (updatedDateOnly.getTime() >= today.getTime()) {
        groups.get('Today')!.push(conv);
      } else if (updatedDateOnly.getTime() >= yesterday.getTime()) {
        groups.get('Yesterday')!.push(conv);
      } else if (updatedDateOnly.getTime() >= startOfThisWeek.getTime()) {
        groups.get('This Week')!.push(conv);
      } else if (updatedDateOnly.getTime() >= startOfLastWeek.getTime()) {
        groups.get('Last Week')!.push(conv);
      } else if (updatedDateOnly.getTime() >= startOfTwoWeeksAgo.getTime()) {
        groups.get('Two Weeks Ago')!.push(conv);
      } else if (updatedDateOnly.getTime() >= startOfThisMonth.getTime()) {
        groups.get('This Month')!.push(conv);
      } else if (updatedDateOnly.getTime() >= startOfLastMonth.getTime()) {
        groups.get('Last Month')!.push(conv);
      } else {
        groups.get('Older')!.push(conv);
      }
    });

    // Convert Map to object for rendering
    return Object.fromEntries(groups);
  }, [conversations, historySearch]);

  // Get visible conversations from context tab IDs
  const visibleConversations = useMemo(() => {
    return visibleTabIds
      .map(id => conversations.find(c => c.conversationId === id))
      .filter(Boolean) as typeof conversations;
  }, [visibleTabIds, conversations]);

  const handleNewChat = async () => {
    try {
      // No need to clear messages -- createConversation creates a fresh buffer
      await createConversation();
    } catch (error) {
      logger.error('Failed to create conversation:', error);
    }
  };

  const handleHideTab = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    // Use context function - it handles fallback switching
    removeVisibleTab(id, true);
  };

  const handleDeleteConversation = async (id: string) => {
    // Context deleteConversation removes from visible tabs
    await deleteConversation(id);
  };

  const handleRename = async (id: string) => {
    if (renameValue.trim()) {
      await renameConversation(id, renameValue.trim());
    }
    setRenamingId(null);
    setRenameValue('');
  };

  const startRename = (e: React.MouseEvent, id: string, currentTitle: string) => {
    e.stopPropagation();
    setRenamingId(id);
    setRenameValue(currentTitle);
    setContextMenuTab(null);
  };

  const handleContextMenu = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenuTab(id);
    setContextMenuPosition({ x: e.clientX, y: e.clientY });
  };

  const handleGenerateTitle = async (id: string) => {
    setContextMenuTab(null);
    try {
      const data = await conversationsApi.generateTitle(id);
      if (data && data.title) {
        await renameConversation(id, data.title);
      }
    } catch (error) {
      logger.error('Failed to generate title:', error);
    }
  };

  const handleClickOutside = () => {
    setContextMenuTab(null);
    setShowHistoryDropdown(false);
  };

  // Handle unlinking a conversation from its artifacts
  const handleUnlinkConversation = async (conversationId: string) => {
    try {
      // Use context function - handles API, state updates, and color cleanup
      await unlinkConversation(conversationId);
      logger.debug('[ConversationTabs] Unlinked conversation:', conversationId);
    } catch (error) {
      logger.error('[ConversationTabs] Failed to unlink:', error);
    }
    setContextMenuTab(null);
  };

  const setTabRef = (conversationId: string) => (el: HTMLDivElement | null) => {
    tabRefs.current[conversationId] = el;
  };

  const scrollTabIntoView = (conversationId: string, position: 'center' | 'end' = 'center') => {
    const tab = tabRefs.current[conversationId];
    if (tab) {
      tab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: position });
    }
  };

  // Scroll active tab into view when it changes
  useEffect(() => {
    if (activeConversationId) {
      scrollTabIntoView(activeConversationId, 'end');
    }
  }, [activeConversationId]);

  // Close history dropdown when clicking outside
  useEffect(() => {
    if (!showHistoryDropdown) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isOutsideDropdown = historyDropdownRef.current && !historyDropdownRef.current.contains(target);
      const isOutsideButton = historyButtonRef.current && !historyButtonRef.current.contains(target);
      
      if (isOutsideDropdown && isOutsideButton) {
        setShowHistoryDropdown(false);
      }
    };

    // Use mousedown for immediate response
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showHistoryDropdown]);

  const handleHistorySelect = (conversationId: string) => {
    switchConversation(conversationId);
    setShowHistoryDropdown(false);

    if (!visibleTabIds.includes(conversationId)) {
      addVisibleTab(conversationId);
    } else {
      scrollTabIntoView(conversationId);
    }
  };

  const handleDragStart = (e: React.DragEvent, conversationId: string) => {
    setDraggedTabId(conversationId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, conversationId: string) => {
    e.preventDefault();
    if (draggedTabId === conversationId) {
      setDropTargetId(null);
      return;
    }
    setDropTargetId(conversationId);
  };
  
  const handleDragLeave = (e: React.DragEvent) => {
    // Only clear if leaving the tab bar area entirely
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (!relatedTarget?.closest('[data-tab-container]')) {
      setDropTargetId(null);
    }
  };

  const handleDrop = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    setDropTargetId(null);
    if (!draggedTabId || draggedTabId === targetId) {
      setDraggedTabId(null);
      return;
    }

    // Calculate new order
    const ids = [...visibleTabIds];
    const fromIndex = ids.indexOf(draggedTabId);
    const toIndex = ids.indexOf(targetId);
    if (fromIndex === -1 || toIndex === -1) {
      setDraggedTabId(null);
      return;
    }
    
    ids.splice(fromIndex, 1);
    ids.splice(toIndex, 0, draggedTabId);
    
    // Use context function to reorder and persist
    reorderVisibleTabs(ids);
    setDraggedTabId(null);
  };

  const handleDragEnd = () => {
    setDraggedTabId(null);
    setDropTargetId(null);
  };

  const handleHistoryScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    const scrolledToBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 100;
    
    if (scrolledToBottom && hasMoreConversations && !loadingMoreConversations) {
      loadMoreConversations();
    }
  };

  // Don't render tabs until conversations are loaded
  if (!conversationsLoaded) {
    return (
      <div className="flex items-center bg-background h-[45px]">
        <div className="px-4 text-xs text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="flex items-center bg-background h-[45px] transition-colors" onClick={handleClickOutside}>
      {/* Scrollable tabs container */}
      <div
        data-tab-container
        role="tablist"
        aria-label="Conversation tabs"
        className="flex items-center gap-1 px-2 py-2 overflow-x-auto flex-1 min-w-0 overscroll-contain" 
        onDragLeave={handleDragLeave}
      >
        {conversations.length === 0 ? (
          <EmptyState
            title="No conversations yet"
            description="Start a conversation to begin collaborating with Aerotage AI."
            compact
          />
        ) : visibleConversations.map((conv) => {
          const isDragging = conv.conversationId === draggedTabId;
          const isDropTarget = conv.conversationId === dropTargetId && draggedTabId && conv.conversationId !== draggedTabId;
          
          // Link-based architecture: show linked artifact name as tab title
          const links = conv.links || [];
          const primaryLink = links[0]; // Use first link for display
          
          // Use artifact colors for linked conversations (visual pairing with document tabs)
          const isLinked = primaryLink != null;
          
          // Get artifact color classes if linked
          const artifactClasses = isLinked ? getClassesForArtifact(primaryLink.artifactId) : null;
          
          // Tab title: show linked artifact info if linked, otherwise conversation title
          // For documents/plans: use artifactId (document number is user-friendly, e.g., NAV-100-SVP-001 or PLAN-ABC123)
          // For diagrams/SSAs: use artifactName (internal IDs are not user-friendly)
          const getLinkedDisplayTitle = () => {
            if (!primaryLink) return conv.title;
            if (primaryLink.artifactType === 'document' || primaryLink.artifactType === 'plan') {
              return primaryLink.artifactId; // Document/plan number (e.g., NAV-100-SVP-001 or PLAN-ABC123)
            }
            return primaryLink.artifactName || primaryLink.artifactId; // Title for diagrams/SSAs
          };
          const displayTitle = getLinkedDisplayTitle();
          
          const isActive = conv.conversationId === activeConversationId;
          const isBufferProcessing = conversationBuffers.get(conv.conversationId)?.isProcessing ?? false;
          
          return (
          <div
            key={conv.conversationId}
            ref={setTabRef(conv.conversationId)}
            // Outer wrapper for larger drop zone
            className="relative flex-shrink-0"
            onDragOver={(e) => handleDragOver(e, conv.conversationId)}
            onDrop={(e) => handleDrop(e, conv.conversationId)}
          >
            {/* Drop indicator line - shows where tab will be inserted */}
            {isDropTarget && (
              <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-info -ml-1 z-10" />
            )}
            <div
            onClick={() => {
              // switchConversation handles stopping active generation before switching
              switchConversation(conv.conversationId);
            }}
            onDoubleClick={(e) => startRename(e, conv.conversationId, conv.title)}
            onContextMenu={(e) => handleContextMenu(e, conv.conversationId)}
            draggable
            onDragStart={(e) => handleDragStart(e, conv.conversationId)}
            onDragEnd={handleDragEnd}
            className={`flex items-center gap-2 px-3 h-8 rounded transition-all duration-150 ${
              isDragging ? 'cursor-grabbing' : 'cursor-pointer'
            } ${
              isDragging ? 'opacity-50 scale-95' : ''
            } ${
              // Linked tabs: active shows bg, inactive just shows border accent
              isLinked && artifactClasses
                ? isActive
                  ? `${artifactClasses.bg} ${artifactClasses.text} shadow-sm border-l-[3px] ${artifactClasses.borderLeft}`
                  : `bg-transparent ${artifactClasses.bgHover} ${artifactClasses.text} border-l-[3px] ${artifactClasses.borderLeft} opacity-70 hover:opacity-100`
                : isActive
                  ? 'bg-info text-info-foreground shadow-sm hover:bg-info/90'
                  : 'bg-card hover:bg-accent text-foreground transition-colors'
            }`}
          >
            {renamingId === conv.conversationId ? (
              <Input
                type="text"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onBlur={() => handleRename(conv.conversationId)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleRename(conv.conversationId);
                  } else if (e.key === 'Escape') {
                    setRenamingId(null);
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                className="w-32 h-6 px-2 text-xs"
                autoFocus
                aria-label="Rename conversation"
              />
            ) : (
              <div className="flex items-center gap-1">
                {/* Show checkmark icon if linked - uses artifact text color */}
                {isLinked && (
                  <svg className="w-3 h-3 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                )}
                {/* Processing indicator for background streaming */}
                {isBufferProcessing && (
                  <span className="w-2 h-2 flex-shrink-0 rounded-full bg-success animate-pulse" title="Streaming in background" />
                )}
                {/* Tab title - inherits text color from parent className */}
                <span className="text-xs font-medium truncate max-w-[150px]">
                  {displayTitle}
                </span>
                {/* Conversation Scope Badge (PRJ/GLB) - uses artifact colors when linked */}
                {((conv as unknown as {scope?: string}).scope === 'project' || (conv as unknown as {scope?: string}).scope === 'global') && (
                  <span className={`text-[10px] px-1 rounded font-medium border ${
                    isLinked && artifactClasses
                      ? `${artifactClasses.text} ${artifactClasses.border} bg-transparent`
                      : conv.conversationId === activeConversationId
                        ? 'bg-white/20 text-white border-white/40'
                        : (conv as unknown as {scope?: string}).scope === 'project'
                          ? 'bg-info/10 border-info/50 text-info'
                          : 'bg-muted text-muted-foreground border-muted-foreground/30'
                  }`}>
                    {(conv as unknown as {scope?: string}).scope === 'project' ? 'PRJ' : 'GLB'}
                  </span>
                )}
                {/* Legacy scopeType badges (for backwards compatibility) */}
                {!(conv as unknown as {scope?: string}).scope && (conv.context as unknown as {scopeType?: string})?.scopeType && (conv.context as unknown as {scopeType?: string}).scopeType !== 'general' && (
                  <span className={`text-[10px] px-1 rounded ${
                    (conv.context as unknown as {scopeType?: string}).scopeType === 'document' 
                      ? 'bg-info-light text-info' 
                      : 'bg-warning-light text-warning'
                  }`}>
                    {(conv.context as unknown as {scopeType?: string}).scopeType === 'document' ? 'DOC' : 'PRJ'}
                  </span>
                )}
                {/* Show +N if multiple artifacts linked */}
                {links.length > 1 && (
                  <span className="text-[10px] text-muted-foreground">
                    +{links.length - 1}
                  </span>
                )}
              </div>
            )}
            
            <Button
              variant="ghost"
              size="xs"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleHideTab(e, conv.conversationId);
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onDragStart={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              draggable={false}
              aria-label={`Hide tab ${displayTitle}`}
              className={`w-4 h-4 p-0 ${
                // Close button inherits artifact text color for theme awareness
                isLinked && artifactClasses
                  ? `${artifactClasses.text} opacity-60 hover:opacity-100`
                  : isActive
                    ? 'text-white/70 hover:text-white hover:bg-white/20'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
              title="Hide tab (conversation stays in history)"
            >
              ✕
            </Button>
            </div>
          </div>
        );
        })}
        
        {/* End drop zone - allows dropping tab at end position */}
        {draggedTabId && (
          <div
            className="w-8 h-8 flex-shrink-0 flex items-center justify-center"
            onDragOver={(e) => {
              e.preventDefault();
              setDropTargetId('__end__');
            }}
            onDragLeave={() => setDropTargetId(null)}
            onDrop={(e) => {
              e.preventDefault();
              if (draggedTabId) {
                const ids = visibleTabIds.filter(id => id !== draggedTabId);
                ids.push(draggedTabId);
                reorderVisibleTabs(ids);
                setDraggedTabId(null);
                setDropTargetId(null);
              }
            }}
          >
            {dropTargetId === '__end__' && (
              <div className="w-0.5 h-6 bg-info rounded" />
            )}
          </div>
        )}
      </div>
      
      {/* History Dropdown Button */}
      <div className="px-2 py-2 flex-shrink-0 relative">
        <Button
          ref={historyButtonRef}
          variant="ghost"
          size="icon"
          onClick={(e) => {
            e.stopPropagation();
            setShowHistoryDropdown(!showHistoryDropdown);
          }}
          title="View all conversations"
          aria-label="View all conversations"
          aria-expanded={showHistoryDropdown}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </Button>

        {/* History Dropdown */}
        {showHistoryDropdown && (
          <div
            ref={historyDropdownRef}
            onScroll={handleHistoryScroll}
            className="absolute right-0 top-full mt-1 w-80 bg-card rounded-lg shadow-xl border border-border max-h-[500px] overflow-y-auto z-[10001]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-card border-b border-border px-4 py-2 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="text-sm font-semibold text-foreground">Conversation History</div>
                  <div className="text-xs text-muted-foreground">{conversations.length}{hasMoreConversations ? '+' : ''} total</div>
                </div>
                <Input
                  type="text"
                  value={historySearch}
                  onChange={(e) => setHistorySearch(e.target.value)}
                  placeholder="Search..."
                  className="h-7 w-32 text-xs"
                  aria-label="Search conversations"
                />
              </div>
            </div>

            {Object.entries(groupedConversations).map(([period, convs]) => {
              if (convs.length === 0) return null;

              return (
                <div key={period} className="border-b border-border last:border-0">
                  <div className="px-4 py-2 bg-muted">
                    <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      {period} ({convs.length})
                    </div>
                  </div>
                  {convs.map(conv => (
                    <div
                      key={conv.conversationId}
                      onClick={() => handleHistorySelect(conv.conversationId)}
                      className={`px-4 py-2 cursor-pointer hover:bg-muted transition-colors ${
                        conv.conversationId === activeConversationId ? 'bg-info-light' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className={`flex items-center gap-1.5 ${
                            conv.conversationId === activeConversationId ? 'text-info' : 'text-foreground'
                          }`}>
                            <span className="text-sm font-medium truncate">
                              {conv.title}
                            </span>
                            {(conv.context as unknown as {scopeType?: string})?.scopeType && (conv.context as unknown as {scopeType?: string}).scopeType !== 'general' && (
                              <span className={`text-[10px] px-1.5 py-0.5 rounded flex-shrink-0 ${
                                (conv.context as unknown as {scopeType?: string}).scopeType === 'document'
                                  ? 'bg-info-light text-info'
                                  : 'bg-warning-light text-warning'
                              }`}>
                                {(conv.context as unknown as {scopeType?: string}).scopeType === 'document' ? 'DOC' : 'PRJ'}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5">
                            {conv.messageCount || 0} messages • {new Date(conv.updatedAt).toLocaleDateString()}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="xs"
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const confirmed = await confirm({
                              title: 'Archive Conversation',
                              description: `Archive "${conv.title}"? It will be removed from your history.`,
                              confirmText: 'Archive',
                              variant: 'destructive',
                            });
                            if (confirmed) {
                              handleDeleteConversation(conv.conversationId);
                            }
                          }}
                          className="w-5 h-5 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive-light"
                          title="Archive conversation"
                          aria-label={`Archive conversation ${conv.title}`}
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
            
            {/* Loading More Indicator */}
            {loadingMoreConversations && (
              <div className="px-4 py-3 text-center text-xs text-muted-foreground">
                Loading more conversations...
              </div>
            )}
            
            {/* End of List Indicator */}
            {!hasMoreConversations && !loadingMoreConversations && conversations.length > 0 && (
              <div className="px-4 py-2 text-center text-xs text-muted-foreground">
                All conversations loaded
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Pinned New button */}
      <div className="px-2 py-2 flex-shrink-0">
        <Button
          size="sm"
          onClick={handleNewChat}
        >
          + New
        </Button>
      </div>

      {/* Context Menu */}
      {contextMenuTab && contextMenuPosition && (
        <div
          className="fixed bg-card rounded shadow-lg border border-border py-1 z-[10001]"
          // eslint-disable-next-line no-restricted-syntax -- positioning requires inline styles
          style={{
            left: `${contextMenuPosition.x}px`,
            top: `${contextMenuPosition.y}px`,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            variant="ghost"
            onClick={() => {
              const conv = conversations.find(c => c.conversationId === contextMenuTab);
              if (conv) {
                startRename({ stopPropagation: () => {} } as React.MouseEvent, contextMenuTab, conv.title);
              }
            }}
            className="w-full justify-start rounded-none px-4 py-2 h-auto text-sm"
          >
            Rename
          </Button>
          <Button
            variant="ghost"
            onClick={() => handleGenerateTitle(contextMenuTab)}
            className="w-full justify-start rounded-none px-4 py-2 h-auto text-sm"
          >
            Generate New Title
          </Button>
          {/* Show Unlink option only for linked conversations */}
          {(() => {
            const conv = conversations.find(c => c.conversationId === contextMenuTab);
            const hasLinks = conv?.links && conv.links.length > 0;
            if (!hasLinks) return null;
            return (
              <>
                <div className="border-t border-border my-1"></div>
                <Button
                  variant="ghost"
                  onClick={() => handleUnlinkConversation(contextMenuTab)}
                  className="w-full justify-start rounded-none px-4 py-2 h-auto text-sm text-warning hover:bg-warning-light"
                  aria-label="Unlink from document"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                  Unlink from Document
                </Button>
              </>
            );
          })()}
          <div className="border-t border-border my-1"></div>
          <Button
            variant="ghost"
            onClick={async () => {
              if (contextMenuTab) {
                const conv = conversations.find(c => c.conversationId === contextMenuTab);
                if (conv) {
                  const confirmed = await confirm({
                    title: 'Archive Conversation',
                    description: `Archive "${conv.title}"? It will be removed from your history.`,
                    confirmText: 'Archive',
                    variant: 'destructive',
                  });
                  if (confirmed) {
                    handleDeleteConversation(contextMenuTab);
                  }
                }
              }
              setContextMenuTab(null);
            }}
            className="w-full justify-start rounded-none px-4 py-2 h-auto text-sm text-destructive hover:bg-destructive-light"
            aria-label="Archive conversation"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
            </svg>
            Archive
          </Button>
        </div>
      )}
    </div>
  );
}
